import { ProviderConfig, AdapterType } from "../../core/types";
import {
  PROVIDER_PRESETS,
  ProviderPreset,
  ProviderPresetRegion,
  presetShortLabel,
  getPresetProviderName,
  findPresetMatch,
} from "../../core/providerPresets";
import {
  saveProvider,
  genId,
  getDefaultMaxContextTokens,
  isModelSpecificMaxContext,
  getDefaultMaxOutputTokens,
  isModelSpecificMaxOutput,
} from "../../core/storage";
import { listModels } from "../../core/adapters";
import { getStrings } from "../../core/i18n";
import { escapeAttr, escapeHtml, wireInfoIcons, labelWithInfo, infoIcon } from "./domUtils";
import { createModelCombobox, ModelComboboxHandle } from "./modelCombobox";

export interface ProviderFormHandlers {
  onSaved: () => void;
  onCancel: () => void;
}

// One form, reused for add and edit.
export function renderProviderForm(
  container: HTMLElement,
  existing: ProviderConfig | null,
  handlers: ProviderFormHandlers
): void {
  const s = getStrings();
  container.innerHTML = `
    <div class="screen provider-form-screen">
      <div class="screen-header">
        <h2>${existing ? s.providerForm.titleEdit : s.providerForm.titleAdd}</h2>
      </div>
      <form id="provider-form">
        <label>${s.providerForm.displayName}
          <input type="text" name="name" value="${existing ? escapeAttr(existing.name) : ""}" />
        </label>
        <label>${s.providerForm.aiService}
          <select name="preset">
            ${PROVIDER_PRESETS.map((p) => `<option value="${p.key}">${p.label}</option>`).join("")}
          </select>
        </label>
        <label>${s.providerForm.adapterType}
          <select name="adapterType">
            <option value="openai-compatible">${s.providerForm.adapterOpenAiCompatible}</option>
            <option value="gemini">${s.providerForm.adapterGemini}</option>
            <option value="anthropic">${s.providerForm.adapterAnthropic}</option>
          </select>
        </label>
        <label>${s.providerForm.apiEndpoint}
          <div class="endpoint-combobox">
            <input
              type="text"
              name="endpoint"
              required
              autocomplete="off"
              value="${existing ? escapeAttr(existing.endpoint) : ""}"
            />
            <button
              type="button"
              class="combobox-toggle"
              id="endpoint-combobox-toggle"
              aria-label="${s.providerForm.showEndpointListAria}"
              style="display:none;"
            >&#9662;</button>
            <ul class="endpoint-suggestions" role="listbox" style="display:none;"></ul>
          </div>
        </label>
        <label><span class="label-row"><span>${s.providerForm.apiKey}</span><span id="api-key-link-wrap" class="field-hint"></span>${infoIcon(
          s.providerForm.apiKeyInfo
        )}</span>
          <input type="password" name="apiKey" required value="${existing ? escapeAttr(existing.apiKey) : ""}" autocomplete="off" />
        </label>
        <label>${s.providerForm.model}
          <div id="model-combobox-container"></div>
        </label>
        <label>${labelWithInfo(s.providerForm.temperature, s.providerForm.temperatureInfo)}
          <input
            type="number"
            name="temperature"
            min="0"
            max="2"
            step="0.1"
            placeholder="${s.providerForm.modelDefault("")}"
            value="${existing && existing.temperature !== undefined ? existing.temperature : ""}"
          />
        </label>
        <label>${labelWithInfo(s.providerForm.maxContext, s.providerForm.maxContextInfo)}
          <input
            type="number"
            name="maxContextTokens"
            min="1"
            step="1"
            value="${existing && existing.maxContextTokens !== undefined ? existing.maxContextTokens : ""}"
          />
        </label>
        <label>${labelWithInfo(s.providerForm.systemPrompt, s.providerForm.systemPromptInfo)}
          <textarea name="systemPrompt" rows="3" placeholder="${escapeAttr(s.providerForm.systemPromptPlaceholder)}">${existing && existing.systemPrompt ? escapeHtml(existing.systemPrompt) : ""}</textarea>
        </label>
        <label id="max-output-tokens-label">${labelWithInfo(s.providerForm.maxOutput, s.providerForm.maxOutputInfo)}
          <input
            type="number"
            name="maxOutputTokens"
            min="1"
            step="1"
            value="${existing && existing.maxOutputTokens !== undefined ? existing.maxOutputTokens : ""}"
          />
        </label>
        <p id="form-error" class="error-text" style="display:none;"></p>
        <div class="form-actions" id="form-actions-main">
          <button type="submit" class="primary-btn">${s.common.save}</button>
          <button type="button" id="cancel-btn" class="link-btn">${s.common.cancel}</button>
        </div>
        <div id="save-confirm" class="save-confirm" style="display:none;">
          <p class="save-confirm-text" id="save-confirm-cost-text"></p>
          <p class="save-confirm-text">${s.providerForm.saveConfirmAgree}</p>
          <div class="form-actions">
            <button type="button" id="save-confirm-yes" class="primary-btn">${s.providerForm.agreeSaveProvider}</button>
            <button type="button" id="save-confirm-cancel" class="secondary-btn">${s.common.cancel}</button>
          </div>
        </div>
      </form>
    </div>
  `;

  wireInfoIcons(container);

  const form = container.querySelector("#provider-form") as HTMLFormElement;
  const presetSelect = form.elements.namedItem("preset") as HTMLSelectElement;
  const adapterSelect = form.elements.namedItem("adapterType") as HTMLSelectElement;
  const endpointInput = form.elements.namedItem("endpoint") as HTMLInputElement;
  const endpointComboboxToggle = container.querySelector(
    "#endpoint-combobox-toggle"
  ) as HTMLButtonElement;
  const endpointSuggestionsList = container.querySelector(
    ".endpoint-suggestions"
  ) as HTMLUListElement;
  const nameInput = form.elements.namedItem("name") as HTMLInputElement;
  const apiKeyInput = form.elements.namedItem("apiKey") as HTMLInputElement;
  const temperatureInput = form.elements.namedItem("temperature") as HTMLInputElement;
  const maxContextTokensInput = form.elements.namedItem("maxContextTokens") as HTMLInputElement;
  const systemPromptInput = form.elements.namedItem("systemPrompt") as HTMLTextAreaElement;
  const maxOutputTokensInput = form.elements.namedItem("maxOutputTokens") as HTMLInputElement;
  const maxOutputTokensLabel = container.querySelector("#max-output-tokens-label") as HTMLElement;
  const errorEl = container.querySelector("#form-error") as HTMLElement;
  const apiKeyLinkWrap = container.querySelector("#api-key-link-wrap") as HTMLElement;
  const modelComboboxContainer = container.querySelector(
    "#model-combobox-container"
  ) as HTMLElement;
  const formActionsMain = container.querySelector("#form-actions-main") as HTMLElement;
  const saveConfirmEl = container.querySelector("#save-confirm") as HTMLElement;
  const saveConfirmCostTextEl = container.querySelector("#save-confirm-cost-text") as HTMLElement;
  // The plain (non-combobox) fields, disabled while the cost-acknowledgment confirmation is
  // showing — otherwise the author could edit a field after triggering Save but before
  // confirming, and clicking "I Agree" would then save the earlier, now-stale snapshot rather
  // than what's currently on screen. Doesn't cover the model combobox (a custom widget, not a
  // plain form control) — a real but small residual gap, not worth the extra plumbing for.
  const fieldsToLockDuringConfirm: (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[] =
    [
      nameInput,
      presetSelect,
      adapterSelect,
      endpointInput,
      apiKeyInput,
      temperatureInput,
      maxContextTokensInput,
      systemPromptInput,
      maxOutputTokensInput,
    ];

  function showError(message: string): void {
    errorEl.textContent = message;
    errorEl.style.display = "";
  }

  function hideError(): void {
    errorEl.style.display = "none";
  }

  const modelCombobox: ModelComboboxHandle = createModelCombobox(modelComboboxContainer, {
    initialValue: existing ? existing.model : "",
    placeholder: s.providerForm.modelPlaceholder,
    fetchModels: () =>
      listModels({
        endpoint: endpointInput.value.trim(),
        apiKey: apiKeyInput.value.trim(),
        adapterType: adapterSelect.value as AdapterType,
      }),
    onError: showError,
    onErrorClear: hideError,
    getFetchBlockedReason: () =>
      !endpointInput.value.trim() || !apiKeyInput.value.trim()
        ? s.providerForm.fetchBlockedReason
        : null,
    onValueChange: () => {
      updateMaxContextPlaceholder();
      updateMaxOutputPlaceholder();
    },
  });

  // Tracks the endpoint+key+adapter combo already auto-fetched, so tabbing back and forth
  // between fields without changing anything doesn't keep re-firing requests.
  let lastAutoFetchKey: string | null = null;

  async function autoFetchOnBlur(): Promise<void> {
    const endpoint = endpointInput.value.trim();
    const apiKey = apiKeyInput.value.trim();
    if (!endpoint || !apiKey) return;

    const fetchKey = `${adapterSelect.value}|${endpoint}|${apiKey}`;
    if (fetchKey === lastAutoFetchKey) return;
    lastAutoFetchKey = fetchKey;

    await modelCombobox.refetch();
    // Real bug, 2026-08-13: without this, the two placeholders below stay frozen at whatever they
    // showed at the moment this function was called (Default: 4,096 for Max reply length on an
    // existing Anthropic provider — computed before any live data existed, since this fetch is
    // async and modelCombobox.refetch() alone never notifies anyone once it resolves). Reopening
    // Edit on an already-saved Claude provider looked like the live per-model number had been
    // lost, even though fetchModelList() had, by then, actually populated fetchedModels with it —
    // nothing ever re-read it into the placeholder. onValueChange (passed to createModelCombobox)
    // only fires on a typed/picked value change, never on a background refetch completing, so this
    // is the one place that needs to do it explicitly.
    updateMaxContextPlaceholder();
    updateMaxOutputPlaceholder();
  }

  apiKeyInput.addEventListener("blur", () => {
    void autoFetchOnBlur();
  });
  endpointInput.addEventListener("blur", () => {
    void autoFetchOnBlur();
  });

  // Same "placeholder, not a real value" approach as the name field above — always tracks the
  // current adapter/endpoint *and* model (live number if the provider's API returned one for
  // this exact model, else a name-pattern estimate — see getDefaultMaxContextTokens), and is
  // simply ignored the moment the author types a real number. Wired to run on every adapter/
  // endpoint change (below) and on every model change (modelCombobox's onValueChange above).
  // "Model default" (not just "Default"), 2026-08-11 — this number really is derived from the
  // selected model (live-fetched or name-pattern-estimated), so naming it after where it
  // actually comes from is more accurate, not just friendlier. Same reasoning applied to
  // Temperature's placeholder below, changed from "Provider default" to "Model default" the same
  // day — "Provider" already means something specific in this app (the saved connection profile
  // this whole form edits), so using it to also mean "the AI service's own default behavior"
  // read as self-referential. **Refined 2026-08-12**: "Model default" is only accurate when the
  // number actually came from tier 1/2 (live or name-pattern) — the tier 3 conservative floor
  // isn't really that model's number, just a safe guess for an unrecognized one, so that case
  // now shows the plain "Default" wording instead (see isModelSpecificMaxContext, storage.ts).
  function updateMaxContextPlaceholder(): void {
    const adapterType = adapterSelect.value as AdapterType;
    const endpoint = endpointInput.value.trim();
    const model = modelCombobox.getValue();
    const liveContextTokens = modelCombobox.getContextTokensFor(model);
    const defaultTokens = getDefaultMaxContextTokens(
      { adapterType, endpoint },
      { model, liveContextTokens }
    );
    const label = isModelSpecificMaxContext({ adapterType, endpoint }, { model, liveContextTokens })
      ? s.providerForm.modelDefault
      : s.providerForm.genericDefault;
    maxContextTokensInput.placeholder = label(defaultTokens.toLocaleString());
  }

  // Only Anthropic requires an explicit reply-length cap (see adapters/anthropic.ts) — showing
  // this field for Gemini/OpenAI-compatible providers would suggest it does something there too.
  // Wired to run everywhere adapterType can change, same call sites as updateMaxContextPlaceholder.
  function updateMaxOutputTokensVisibility(): void {
    maxOutputTokensLabel.style.display = adapterSelect.value === "anthropic" ? "" : "none";
  }

  // Added 2026-08-11 alongside listModels() learning to read max_tokens (see anthropic.ts) —
  // before this, blank always meant the same flat DEFAULT_ANTHROPIC_MAX_TOKENS (4096) fallback
  // for every model, which is why this field used to need manually raising for anything long
  // (current Claude models support far more). "Model default" when a live fetch actually
  // returned this exact model's number; **refined 2026-08-12** to fall back to the plain
  // "Default" wording for the flat DEFAULT_ANTHROPIC_MAX_TOKENS case instead, same reasoning as
  // Max context's own placeholder above — that flat number isn't derived from the selected
  // model at all, so labeling it "Model default" overstated what it actually is.
  function updateMaxOutputPlaceholder(): void {
    const liveMaxOutputTokens = modelCombobox.getMaxOutputTokensFor(modelCombobox.getValue());
    const defaultTokens = getDefaultMaxOutputTokens({ liveMaxOutputTokens });
    const label = isModelSpecificMaxOutput({ liveMaxOutputTokens })
      ? s.providerForm.modelDefault
      : s.providerForm.genericDefault;
    maxOutputTokensInput.placeholder = label(defaultTokens.toLocaleString());
  }

  // No known console page for "Custom / other" or any saved provider that doesn't match a
  // known preset (unrecognized endpoint) — an arbitrary/unlisted API has nowhere specific to
  // send the author, so the hint is simply omitted rather than showing a guessed/generic link.
  // Takes the URL directly (not a whole preset) since the effective one now depends on which
  // endpoint option was picked, for a preset that has more than one (see applyEndpointOption
  // below).
  function updateApiKeyLink(url: string | undefined): void {
    if (!url) {
      apiKeyLinkWrap.innerHTML = "";
      apiKeyLinkWrap.style.display = "none";
      return;
    }
    apiKeyLinkWrap.innerHTML = `(${s.providerForm.getApiKey}: <a target="_blank" href="${escapeAttr(url)}">${escapeHtml(url)}</a>)`;
    apiKeyLinkWrap.style.display = "";
  }

  // The endpoint field's dropdown arrow — shown only when the current preset has more than one
  // known endpoint (currently only Kimi/Qwen's `regions`, see providerPresets.ts). A single-
  // endpoint preset (or "Custom / other", or no matched preset at all) hides the arrow entirely;
  // the plain text input underneath is always editable regardless, so nothing about typing a
  // custom endpoint changes based on this. Kept as a small local list (not model-combobox.ts's
  // fetch machinery) since this is a fixed 0-2 item list known synchronously, never fetched.
  let currentEndpointOptions: ProviderPresetRegion[] = [];

  function renderEndpointOptions(preset: ProviderPreset | undefined): void {
    currentEndpointOptions = preset?.regions ?? [];
    endpointComboboxToggle.style.display = currentEndpointOptions.length > 1 ? "" : "none";
    // data-endpoint is enough to look the full option back up in currentEndpointOptions (below)
    // when clicked — no need to also stash apiKeyUrl on the element itself.
    endpointSuggestionsList.innerHTML = currentEndpointOptions
      .map(
        (r) =>
          `<li class="endpoint-suggestion" data-endpoint="${escapeAttr(r.endpoint)}">${escapeHtml(r.label)} — ${escapeHtml(r.endpoint)}</li>`
      )
      .join("");
    endpointSuggestionsList.style.display = "none";
  }

  // Fills the endpoint field and API key link from one specific known option — called when the
  // author explicitly picks one from the dropdown, or (via applyPreset below) on an explicit
  // preset switch, defaulting to `regions[0]` (International). Deliberately **not** called from
  // the edit-form load path below when the existing provider's own endpoint already matches an
  // option — that path only needs the API key link to catch up, not to overwrite an endpoint
  // that's already correct (and may differ character-for-character, e.g. a trailing slash,
  // from the option it matched, without that being a real reason to change it).
  function applyEndpointOption(option: ProviderPresetRegion): void {
    endpointInput.value = option.endpoint;
    updateApiKeyLink(option.apiKeyUrl);
  }

  function applyPreset(preset: ProviderPreset): void {
    adapterSelect.value = preset.adapterType;
    // A known preset fully determines its adapter type — only "Custom / other" leaves it
    // as a real choice, since we have no way to know what shape an unlisted API speaks.
    adapterSelect.disabled = preset.key !== "custom";
    renderEndpointOptions(preset);
    // Always applies, add or edit — this only ever runs from an explicit user action (picking
    // the initial default for a brand-new provider, or changing the Preset dropdown), never on
    // the edit form's initial render (that path sets fields directly, without calling this), so
    // there's no case where overwriting the endpoint here isn't actually what was just asked for.
    if (preset.regions && preset.regions.length > 0) {
      // regions[0] is always the default (International) — see providerPresets.ts's own comment.
      applyEndpointOption(preset.regions[0]);
    } else {
      endpointInput.value = preset.endpoint;
      updateApiKeyLink(preset.apiKeyUrl);
    }
    // A placeholder (not a real value) so it reads as a suggestion, not real input: it always
    // tracks the current preset, but the moment the author types their own name, .value stops
    // being empty and this stops being shown or used — switching presets after that never
    // overwrites what they typed.
    nameInput.placeholder = presetShortLabel(preset.label);
    updateMaxContextPlaceholder();
    updateMaxOutputTokensVisibility();
    updateMaxOutputPlaceholder();
  }

  if (existing) {
    adapterSelect.value = existing.adapterType;
    const match = findPresetMatch(existing.endpoint, existing.adapterType);
    presetSelect.value = match ? match.preset.key : "custom";
    adapterSelect.disabled = match ? match.preset.key !== "custom" : false;
    renderEndpointOptions(match?.preset);
    // The endpoint field is already correct (set from `existing.endpoint` in the markup above) —
    // only the API key link needs to catch up here, from whichever specific option (if any) it
    // actually matched, not just the preset's own default.
    updateApiKeyLink(
      (match?.region ?? match?.preset.regions?.[0])?.apiKeyUrl ?? match?.preset.apiKeyUrl
    );
    updateMaxContextPlaceholder();
    updateMaxOutputTokensVisibility();
    updateMaxOutputPlaceholder();
    // Fields are already filled in on the edit form, so no blur event will fire to trigger it.
    void autoFetchOnBlur();
  } else {
    // Default to Gemini for a brand-new provider, 2026-08-18 — explicit request. Picked by key,
    // not array position, since PROVIDER_PRESETS stays alphabetical (its own comment) for the
    // dropdown's display order; this default is independent of where Gemini happens to sort.
    const defaultPreset = PROVIDER_PRESETS.find((p) => p.key === "gemini") ?? PROVIDER_PRESETS[0];
    presetSelect.value = defaultPreset.key;
    applyPreset(defaultPreset);
  }

  presetSelect.addEventListener("change", () => {
    const preset = PROVIDER_PRESETS.find((p) => p.key === presetSelect.value);
    if (preset) applyPreset(preset);
  });

  // Same interaction shape as modelCombobox.ts's own toggle/suggestions (mousedown
  // preventDefault on the toggle so its click isn't preempted by the input's blur-triggered
  // hide, a short delay before hiding on blur so a suggestion's own mousedown can land first),
  // minus the fetch machinery — this list is always already known, nothing to fetch.
  let hideEndpointSuggestionsTimeoutId: number | undefined;

  function hideEndpointSuggestions(): void {
    endpointSuggestionsList.style.display = "none";
  }

  function scheduleHideEndpointSuggestions(): void {
    hideEndpointSuggestionsTimeoutId = window.setTimeout(() => {
      hideEndpointSuggestions();
      hideEndpointSuggestionsTimeoutId = undefined;
    }, 150);
  }

  function cancelScheduledHideEndpointSuggestions(): void {
    if (hideEndpointSuggestionsTimeoutId !== undefined) {
      window.clearTimeout(hideEndpointSuggestionsTimeoutId);
      hideEndpointSuggestionsTimeoutId = undefined;
    }
  }

  endpointComboboxToggle.addEventListener("mousedown", (e) => {
    e.preventDefault();
  });

  endpointComboboxToggle.addEventListener("click", () => {
    cancelScheduledHideEndpointSuggestions();
    endpointSuggestionsList.style.display =
      endpointSuggestionsList.style.display === "none" ? "" : "none";
  });

  endpointInput.addEventListener("blur", () => {
    scheduleHideEndpointSuggestions();
  });

  // mousedown (not click) fires before the input's blur, so the value swap below happens
  // before blur's hide-timeout would otherwise race it.
  endpointSuggestionsList.addEventListener("mousedown", (e) => {
    const target = e.target as HTMLElement;
    if (!target.classList.contains("endpoint-suggestion")) return;
    e.preventDefault();
    const option = currentEndpointOptions.find(
      (r) => r.endpoint === target.getAttribute("data-endpoint")
    );
    if (option) applyEndpointOption(option);
    hideEndpointSuggestions();
  });

  // Only reachable when "Custom / other" leaves adapterSelect enabled (see applyPreset above) —
  // still needs to keep the placeholder/visibility in sync when changed directly, not just via
  // presets.
  adapterSelect.addEventListener("change", () => {
    updateMaxContextPlaceholder();
    updateMaxOutputTokensVisibility();
    updateMaxOutputPlaceholder();
  });

  container.querySelector("#cancel-btn")?.addEventListener("click", handlers.onCancel);

  // Snapshot of the fields' disabled state right before locking for confirmation — needed
  // because adapterSelect's disabled-ness is itself meaningful (tied to whether the current
  // preset is a known one, see applyPreset), so unlocking must restore that, not just force
  // every field back to enabled.
  let disabledStateBeforeConfirm: boolean[] = [];

  function lockFormForConfirm(): void {
    disabledStateBeforeConfirm = fieldsToLockDuringConfirm.map((el) => el.disabled);
    fieldsToLockDuringConfirm.forEach((el) => {
      el.disabled = true;
    });
  }

  function unlockFormAfterCancel(): void {
    fieldsToLockDuringConfirm.forEach((el, i) => {
      el.disabled = disabledStateBeforeConfirm[i] ?? false;
    });
  }

  // Set once validation passes and the confirmation is shown; read by the confirm button's own
  // click handler below. Cleared again on cancel so a stale snapshot can never accidentally get
  // saved by some other path.
  let pendingProvider: ProviderConfig | null = null;

  container.querySelector("#save-confirm-cancel")?.addEventListener("click", () => {
    pendingProvider = null;
    saveConfirmEl.style.display = "none";
    formActionsMain.style.display = "";
    unlockFormAfterCancel();
  });

  container.querySelector("#save-confirm-yes")?.addEventListener("click", () => {
    if (!pendingProvider) return;
    saveProvider(pendingProvider);
    handlers.onSaved();
  });

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    hideError();

    const endpoint = endpointInput.value.trim();
    const apiKey = apiKeyInput.value.trim();
    const model = modelCombobox.getValue();
    const adapterType = adapterSelect.value as AdapterType;

    // Left blank, falls back to the placeholder — kept in sync with the selected preset by
    // applyPreset above, with the "(Company)" disambiguator stripped.
    const name = nameInput.value.trim() || nameInput.placeholder.trim();

    if (!name || !endpoint || !apiKey || !model) {
      showError(s.providerForm.allFieldsRequired);
      return;
    }

    // Catch typos/invalid model names here rather than letting them fail on the first chat
    // message. Only enforced when we actually have a live list to check against — a provider
    // whose model list couldn't be fetched still allows free-form manual entry.
    if (modelCombobox.hasFetchedModels() && !modelCombobox.isKnownModel(model)) {
      showError(s.providerForm.modelNotInList(model));
      return;
    }

    const temperatureRaw = temperatureInput.value.trim();
    let temperature: number | undefined;
    if (temperatureRaw) {
      const parsed = Number(temperatureRaw);
      if (Number.isNaN(parsed) || parsed < 0 || parsed > 2) {
        showError(s.providerForm.temperatureRange);
        return;
      }
      temperature = parsed;
    }

    // Left `undefined` (not resolved to a concrete number) when blank — same "stays genuinely
    // unset, shown via placeholder every time you reopen this form" behavior as Temperature,
    // 2026-08-11 (was previously resolved-and-saved eagerly here; changed for consistency
    // between the two fields, per explicit request). getDefaultMaxContextTokens/
    // getDefaultMaxOutputTokens below still compute the live number for the *placeholder*
    // (updateMaxContextPlaceholder/updateMaxOutputPlaceholder) — this just stops that number
    // from being frozen into the saved provider the moment you leave the field blank, so a
    // later live re-fetch (a provider updating a model's real limit, say) is reflected next
    // time you open this form rather than silently staying stale. providerList.ts's own card
    // display (formatMaxContextTokens) already expected this — it has always shown "(default)"
    // for an unset value, which just couldn't ever actually trigger for a provider saved
    // through this form until now.
    const maxContextTokensRaw = maxContextTokensInput.value.trim();
    let maxContextTokens: number | undefined;
    if (maxContextTokensRaw) {
      const parsed = Number(maxContextTokensRaw);
      if (!Number.isInteger(parsed) || parsed < 1) {
        showError(s.providerForm.maxContextPositive);
        return;
      }
      maxContextTokens = parsed;
    }

    const systemPrompt = systemPromptInput.value.trim() || undefined;

    // Only meaningful (and only shown, see updateMaxOutputTokensVisibility) for Anthropic
    // providers — ignored here for any other adapter type even if a stale value is still
    // sitting in the hidden field from a since-changed adapter selection. Left undefined when
    // blank, same reasoning as maxContextTokens above.
    let maxOutputTokens: number | undefined;
    if (adapterType === "anthropic") {
      const maxOutputTokensRaw = maxOutputTokensInput.value.trim();
      if (maxOutputTokensRaw) {
        const parsed = Number(maxOutputTokensRaw);
        if (!Number.isInteger(parsed) || parsed < 1) {
          showError(s.providerForm.maxOutputPositive);
          return;
        }
        maxOutputTokens = parsed;
      }
    }

    const provider: ProviderConfig = {
      id: existing ? existing.id : genId(),
      name,
      endpoint,
      apiKey,
      adapterType,
      model,
      ...(temperature !== undefined ? { temperature } : {}),
      ...(systemPrompt !== undefined ? { systemPrompt } : {}),
      ...(maxContextTokens !== undefined ? { maxContextTokens } : {}),
      ...(maxOutputTokens !== undefined ? { maxOutputTokens } : {}),
    };

    // Not saved yet — validation passed, but this still has to go through the cost
    // acknowledgment confirmation below (#save-confirm-yes) before saveProvider() actually
    // runs. Native confirm() isn't used here for the same reason providerList.ts's delete
    // confirmation avoids it: unreliable inside Office task panes.
    pendingProvider = provider;
    saveConfirmCostTextEl.textContent = s.providerForm.saveConfirmCost(
      getPresetProviderName({ endpoint, adapterType, name })
    );
    formActionsMain.style.display = "none";
    saveConfirmEl.style.display = "";
    lockFormForConfirm();
  });
}
